import React from "react";

/**
 * Inline formatting supported across the system-message body editor and
 * the live dashboard widget that displays the resulting messages.
 *
 *   1. Markdown link:   [link text](https://example.com)
 *   2. Bare URL:        https://example.com (auto-linkified)
 *   3. Bold:            **bold text**
 *   4. Underline:       __underline text__
 *
 */

export type FormatSpan = {
  type: "bold" | "underline" | "link";
  outerStart: number;
  outerEnd: number;
  innerStart: number;
  innerEnd: number;
  linkText?: string;
  linkUrl?: string;
};

// The link-URL part `[^\s()]*(?:\([^\s)]*\))*[^\s()]*` allows one level of
// balanced parens so URLs like https://en.wikipedia.org/wiki/Foo_(bar) work
// correctly inside markdown-style links. Excluding `(` from the bracketing
// parts (rather than just `)`) is what forces the engine to stop at `(` and
// let the inner `(?:\(...\))*` group match — otherwise greedy matching
// would swallow past the open paren and the inner group would never fire.
// Multi-paren or deeply-nested URLs are not supported; rare enough to
// punt on.
const TOKEN_REGEX =
  /\[([^\]]+)\]\((https?:\/\/[^\s()]*(?:\([^\s)]*\))*[^\s()]*)\)|(https?:\/\/[^\s]+)|\*\*([^*\n]+)\*\*|__([^_\n]+)__/g;

/**
 * Length of the message as users will perceive it. Strips markup wrappers
 * (** for bold, __ for underline, [text](url) keeping just `text`) so the
 * 100-character body limit applies to visible content rather than raw input.
 *
 * Bare URLs are kept as-is because they ARE visible — admins typed a URL,
 * users will see a URL.
 *
 * NOTE: the regexes here intentionally mirror the link / bold / underline
 * sub-patterns of TOKEN_REGEX above. If you add a new token type, update
 * both places. Bare URLs are deliberately omitted from the strip chain.
 */
export function visibleLength(text: string): number {
  return text
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s()]*(?:\([^\s)]*\))*[^\s()]*)\)/g,
      "$1"
    )
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1").length;
}

/**
 * Walks the text and returns every formatting span — including spans nested
 * inside other spans. Used by the editor for toolbar active-state detection
 * (is the cursor inside one of these?) and toggle-off behavior (we need the
 * exact bounds to remove markers).
 */
export function findSpans(text: string, baseOffset = 0): FormatSpan[] {
  const spans: FormatSpan[] = [];

  for (const match of text.matchAll(TOKEN_REGEX)) {
    const [full, mdLabel, mdUrl, bareUrl, boldText, underlineText] = match;
    const start = match.index! + baseOffset;

    if (mdLabel && mdUrl) {
      spans.push({
        type: "link",
        outerStart: start,
        outerEnd: start + full.length,
        innerStart: start + 1,
        innerEnd: start + 1 + mdLabel.length,
        linkText: mdLabel,
        linkUrl: mdUrl,
      });
      spans.push(...findSpans(mdLabel, start + 1));
    } else if (bareUrl) {
      // Bare URLs aren't wrapped — no toolbar state to track for them
    } else if (boldText) {
      spans.push({
        type: "bold",
        outerStart: start,
        outerEnd: start + full.length,
        innerStart: start + 2,
        innerEnd: start + full.length - 2,
      });
      spans.push(...findSpans(boldText, start + 2));
    } else if (underlineText) {
      spans.push({
        type: "underline",
        outerStart: start,
        outerEnd: start + full.length,
        innerStart: start + 2,
        innerEnd: start + full.length - 2,
      });
      spans.push(...findSpans(underlineText, start + 2));
    }
  }

  return spans;
}

/**
 * Renders text as React nodes. Recursive so nested formatting works.
 * Tokens are matched in a single pass; the recursive call on each token's
 * inner content is what makes things like `**__hello__**` render as
 * bold-and-underlined rather than bold containing literal markdown.
 *
 * Only http/https URLs are accepted; React escapes everything else, so
 * scripts and other dangerous content render as literal text.
 */
export function renderRichText(text: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(TOKEN_REGEX)) {
    const [full, mdLabel, mdUrl, bareUrl, boldText, underlineText] = match;
    const start = match.index!;

    if (start > lastIndex) {
      result.push(text.slice(lastIndex, start));
    }

    if (mdLabel && mdUrl) {
      result.push(
        <a key={key++} href={mdUrl} target="_blank" rel="noopener noreferrer">
          {renderRichText(mdLabel)}
        </a>
      );
      lastIndex = start + full.length;
    } else if (bareUrl) {
      const trailingMatch = bareUrl.match(/[.,;:!?)\]]+$/);
      const trailing = trailingMatch ? trailingMatch[0] : "";
      const url = trailing ? bareUrl.slice(0, -trailing.length) : bareUrl;

      result.push(
        <a key={key++} href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      );
      if (trailing) result.push(trailing);
      lastIndex = start + full.length;
    } else if (boldText) {
      result.push(<strong key={key++}>{renderRichText(boldText)}</strong>);
      lastIndex = start + full.length;
    } else if (underlineText) {
      result.push(<u key={key++}>{renderRichText(underlineText)}</u>);
      lastIndex = start + full.length;
    }
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}
